import { type CSSProperties, type ReactNode, type MouseEventHandler } from "react";

export type ButtonVariant = "primary" | "secondary" | "outline" | "ghost" | "dest";
export type ButtonSize = "sm" | "md" | "lg" | "xl";

export interface ButtonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: ReactNode;
  children?: ReactNode;
  href?: string;
  onClick?: MouseEventHandler<HTMLElement>;
  fullWidth?: boolean;
  disabled?: boolean;
  loading?: boolean;
  type?: "button" | "submit" | "reset";
  "aria-label"?: string;
  style?: CSSProperties;
}

const SIZES: Record<ButtonSize, CSSProperties> = {
  sm: { height: 32, padding: "0 12px", fontSize: 12 },
  md: { height: 40, padding: "0 16px", fontSize: 13 },
  lg: { height: 48, padding: "0 20px", fontSize: 14 },
  xl: { height: 56, padding: "0 28px", fontSize: 15 },
};

const VARIANTS: Record<ButtonVariant, CSSProperties> = {
  primary: { background: "#1845FF", color: "white", border: "1px solid #1845FF" },
  secondary: {
    background: "var(--button-secondary-bg)",
    color: "var(--button-secondary-fg)",
    border: "1px solid var(--button-secondary-border)",
  },
  outline: { background: "transparent", color: "var(--fg)", border: "1px solid var(--border)" },
  ghost: { background: "transparent", color: "var(--fg-muted)", border: "1px solid transparent" },
  dest: { background: "#FF2D1F", color: "white", border: "1px solid #FF2D1F" },
};

/** Brand button. Faithful to brand.jsx, with disabled + loading states added. */
export function Button({
  variant = "primary",
  size = "md",
  icon,
  children,
  href,
  onClick,
  fullWidth,
  disabled = false,
  loading = false,
  type = "button",
  style,
  ...rest
}: ButtonProps) {
  const isDisabled = disabled || loading;
  const composed: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    fontFamily: "Geist, system-ui, sans-serif",
    fontWeight: 500,
    borderRadius: 6,
    cursor: isDisabled ? "not-allowed" : "pointer",
    textDecoration: "none",
    letterSpacing: "-0.005em",
    transition: "background 150ms cubic-bezier(0.16,1,0.3,1), border-color 150ms, opacity 150ms",
    width: fullWidth ? "100%" : undefined,
    opacity: isDisabled ? 0.5 : 1,
    pointerEvents: isDisabled ? "none" : undefined,
    ...SIZES[size],
    ...VARIANTS[variant],
    ...style,
  };

  if (href && !isDisabled) {
    return (
      <a href={href} onClick={onClick} style={composed} {...rest}>
        {loading ? <Spinner /> : icon}
        {children}
      </a>
    );
  }

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      style={composed}
      {...rest}
    >
      {loading ? <Spinner /> : icon}
      {children}
    </button>
  );
}

function Spinner() {
  return (
    <span
      aria-hidden
      style={{
        width: 14,
        height: 14,
        borderRadius: 9999,
        border: "2px solid currentColor",
        borderTopColor: "transparent",
        display: "inline-block",
        animation: "vocSpin 0.7s linear infinite",
      }}
    />
  );
}
